import React, { useState, useRef, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Badge,
  Box,
  InputBase,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Paper,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  ShoppingCart as CartIcon,
  ShoppingCart,
  FavoriteBorder as WishlistIcon,
  Person as PersonIcon,
  Menu as MenuIcon,
  Home as HomeIcon,
  Category as CategoryIcon,
  AdminPanelSettings as AdminIcon,
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
  Speed as SpeedIcon,
  Inventory2 as Inventory2Icon,
  ReceiptLong as ReceiptLongIcon,
  Mic as MicIcon,
  Stop as StopIcon,
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';


import { logoutUser } from '../../store/slices/authSlice';
import { selectCartItemCount } from '../../store/slices/cartSlice';
import { toggleMobileMenu, setSearchQuery } from '../../store/slices/uiSlice';
import NotificationSystem from '../notifications/NotificationSystem';
import { getFullImageUrl } from '../../utils/imageUtils';
import { productService } from '../../services/productService';

// Styled components (removed unused components after logo update)

const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  // Redux state
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const cartItemCount = useSelector(selectCartItemCount);
  const wishlistItemsCount = useSelector((state) => state.customer.wishlistItemsCount);
  const { mobileMenuOpen, searchQuery } = useSelector((state) => state.ui);
  
  // Local state
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchValue, setSearchValue] = useState(searchQuery || '');

  // Autocomplete state
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState([]);
  const latestQueryRef = useRef('');
  const debounceTimerRef = useRef(null);
  const searchContainerRef = useRef(null);

  // Voice search state
  const [isRecording, setIsRecording] = useState(false);
  const [voiceError, setVoiceError] = useState('');
  const [voiceErrorOpen, setVoiceErrorOpen] = useState(false);
  const recognitionRef = useRef(null);
  // Business logo for avatar only - navbar always uses MOS logo
  const businessLogoUrl = user?.businessProfile?.logoUrl
    ? getFullImageUrl(user.businessProfile.logoUrl)
    : null;
  
  // Menu handlers
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleMobileMenuToggle = () => {
    dispatch(toggleMobileMenu());
  };
  
  // Navigation handlers
  const handleLogoClick = () => {
    navigate('/');
  };
  
  const handleLogin = () => {
    navigate('/login');
    handleMenuClose();
  };
  
  const handleRegister = () => {
    navigate('/register');
    handleMenuClose();
  };
  
  const handleLogout = () => {
    dispatch(logoutUser(user?.id));
    handleMenuClose();
    navigate('/');
  };
  
  const handleProfile = () => {
    navigate('/profile');
    handleMenuClose();
  };
  
  const handleDashboard = () => {
    if (user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') {
      navigate('/admin');
    } else if (user?.role === 'SELLER') {
      navigate('/seller');
    } else {
      navigate('/profile');
    }
    handleMenuClose();
  };
  
  const handleCart = () => {
    navigate('/cart');
  };
  
  const handleWishlist = () => {
    navigate('/wishlist');
  };
  
  // Close suggestions when clicking outside the search container
  useEffect(() => {
    const handler = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setAutocompleteSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      clearTimeout(debounceTimerRef.current);
    };
  }, []);

  // Search handlers
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchValue(value);

    const trimmed = value.trim();
    clearTimeout(debounceTimerRef.current);

    if (trimmed.length < 3) {
      setAutocompleteSuggestions([]);
      latestQueryRef.current = '';
      return;
    }

    latestQueryRef.current = trimmed;
    debounceTimerRef.current = setTimeout(async () => {
      const queryAtDispatch = trimmed;
      try {
        const results = await productService.autocompleteProducts(queryAtDispatch);
        if (latestQueryRef.current === queryAtDispatch) {
          setAutocompleteSuggestions(Array.isArray(results) ? results : []);
        }
      } catch {
        // Autocomplete failure is non-fatal; normal search is unaffected
      }
    }, 300);
  };

  const handleSuggestionClick = (suggestion) => {
    clearTimeout(debounceTimerRef.current);
    latestQueryRef.current = '';
    setAutocompleteSuggestions([]);
    setSearchValue(suggestion.name);
    if (suggestion.type === 'GROUP') {
      // Navigate using the exact product IDs returned by autocomplete grouping.
      // This ensures only the grouped products are displayed, not the whole catalog.
      const ids = suggestion.ids;
      if (ids && ids.length > 0) {
        navigate(`/products?product_ids=${ids.join(',')}`);
      } else if (suggestion.query) {
        // Fallback for backwards-compatible GROUP suggestions without ids
        navigate(`/products?group_name=${encodeURIComponent(suggestion.query)}`);
      }
    } else {
      // Navigate directly to product detail using the product ID
      navigate(`/products/${suggestion.id}`);
    }
  };

  const handleSearchSubmit = async (event) => {
    event.preventDefault();
    clearTimeout(debounceTimerRef.current);
    setAutocompleteSuggestions([]);
    const query = searchValue.trim();
    if (query) {
      dispatch(setSearchQuery(query));
      // Use AI search for all queries - it handles simple, complex, and unrelated queries intelligently
      navigate(`/products?ai_search=${encodeURIComponent(query)}`);
    }
  };

  // Voice search handlers
  const showVoiceError = (message) => {
    setVoiceError(message);
    setVoiceErrorOpen(true);
  };

  const handleVoiceMicClick = () => {
    // Stop ongoing recognition if already recording
    if (isRecording) {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      showVoiceError('Voice search is not supported in this browser. Please use Chrome or Edge.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-IN';
    recognition.maxAlternatives = 1;
    recognitionRef.current = recognition;

    recognition.onstart = () => {
      setIsRecording(true);
    };

    recognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      // Show live interim text in the search bar while the user is speaking
      if (interimTranscript) {
        setSearchValue(interimTranscript);
      }

      // Final result: put text in search bar and trigger the existing AI search
      if (finalTranscript) {
        const text = finalTranscript.trim();
        setSearchValue(text);
        if (text) {
          dispatch(setSearchQuery(text));
          navigate(`/products?ai_search=${encodeURIComponent(text)}`);
        }
      }
    };

    recognition.onerror = (event) => {
      setIsRecording(false);
      if (event.error === 'not-allowed') {
        showVoiceError('Microphone access denied. Please allow microphone access in your browser settings.');
      } else if (event.error === 'no-speech') {
        showVoiceError('No speech detected. Please try again.');
      } else if (event.error === 'audio-capture') {
        showVoiceError('No microphone found. Please connect a microphone and try again.');
      } else if (event.error === 'network') {
        showVoiceError('Network error during voice search. Please check your connection.');
      } else {
        showVoiceError('Voice search failed. Please try again.');
      }
    };

    recognition.onend = () => {
      setIsRecording(false);
      recognitionRef.current = null;
    };

    try {
      recognition.start();
    } catch (e) {
      showVoiceError('Could not start voice search. Please try again.');
      setIsRecording(false);
    }
  };
  
  // Optimized navigation structure following modern UX practices
  const getNavigationItems = () => {
    const baseItems = [
     // { label: 'Home', path: '/', icon: <HomeIcon /> },
      //{ label: 'View Products', path: '/products', icon: <CategoryIcon /> },
    ];
    
    if (user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') {
      return [
        ...baseItems,
        { label: 'Dashboard', path: '/admin', icon: <SpeedIcon />, key: 'admin-dashboard' },
        //{ label: 'Management', path: '/admin/management', icon: <AdminIcon />, key: 'admin-management' },
        { label: 'View Products', path: '/products', icon: <Inventory2Icon /> },
      ];
    }
    
    if (user?.role === 'SELLER') {
      return [
        ...baseItems,
        { label: 'Dashboard', path: '/seller', icon: <SpeedIcon />, key: 'seller-dashboard' },
        { label: 'View Orders', path: '/order-management', icon: <ReceiptLongIcon />, key: 'seller-orders' },
        { label: 'View Products', path: '/products', icon: <Inventory2Icon /> },
      ];
    }
    
    // Customer/Guest navigation - removed Home and Products as requested
    const customerItems = [
      // No base navigation items for customers
      ...(isAuthenticated ? [
        //{ label: 'Orders', path: '/orders', icon: <ShoppingCart /> },
        //{ label: 'Wishlist', path: '/wishlist', icon: <WishlistIcon /> },
      ] : []),
    ];
    
    return customerItems;
  };
  
  const navigationItems = getNavigationItems();
  
  // Mobile menu items
  const mobileMenuItems = [
    ...navigationItems,
    ...(isAuthenticated ? [
      { label: 'Profile', path: '/profile', icon: <PersonIcon /> },
      { label: 'Dashboard', action: handleDashboard, icon: <DashboardIcon /> },
      { label: 'Logout', action: handleLogout, icon: <LogoutIcon /> },
    ] : [
      { label: 'Login', path: '/login', icon: <PersonIcon /> },
      { label: 'Register', path: '/register', icon: <PersonIcon /> },
    ]),
  ];
  
  const isMenuOpen = Boolean(anchorEl);
  
  return (
    <>
      <AppBar 
        position="sticky" 
        elevation={0} 
        sx={{ 
          bgcolor: 'white',
          color: 'text.primary',
          borderBottom: '1px solid #e0e0e0',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Toolbar sx={{ px: { xs: 1, sm: 2, md: 3 }, pl: location.pathname.startsWith('/seller') ? 4 : { xs: 1, sm: 2, md: 3 } }}>
          {/* Mobile menu button */}
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={handleMobileMenuToggle}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          {/* Logo */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              gap: 2
            }} 
            onClick={handleLogoClick}
          >
            <Box
              component="img"
              src="/mos-logo.jpg"
              alt="MOS - Stone Shopping Simplified"
              sx={{
                height: { xs: 45, sm: 55, md: 60 },
                width: 'auto',
                borderRadius: '8px',
                objectFit: 'cover',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': {
                  transform: 'scale(1.05)',
                  transition: 'transform 0.2s ease-in-out',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg,#2C3E50,#2C3E50)',
                
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                display: { xs: 'none', sm: 'block' },
                fontSize: { sm: '1.1rem', md: '1.25rem' },
                letterSpacing: '0.5px',
              }}
            >
              MALL OF STONES
            </Typography>
          </Box>
          
          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ ml: 4, display: 'flex', gap: 2 }}>
              {navigationItems.map((item) => (
                <Button
                  key={item.key || item.path}
                  color="inherit"
                  startIcon={item.icon}
                  onClick={() => navigate(item.path)}
                  sx={{
                    mx: 0.5,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: location.pathname === item.path ? 600 : 400,
                    bgcolor: location.pathname === item.path ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
                    color: location.pathname === item.path ? 'primary.main' : 'text.primary',
                    '&:hover': {
                      bgcolor: 'rgba(102, 126, 234, 0.05)',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}
          
          {/* Search Bar */}
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', mx: 2 }}>
            <Box ref={searchContainerRef} sx={{ position: 'relative', maxWidth: 400, width: '100%' }}>
              <Paper
                component="form"
                onSubmit={handleSearchSubmit}
                sx={{
                  p: '2px 4px',
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: 3,
                  bgcolor: '#f5f5f5',
                  border: '1px solid transparent',
                  width: '100%',
                  '&:focus-within': {
                    border: '1px solid #667eea',
                    bgcolor: 'white',
                  },
                }}
              >
                <IconButton sx={{ p: '10px' }} aria-label="search">
                  <SearchIcon />
                </IconButton>
                <InputBase
                  sx={{ ml: 1, flex: 1 }}
                  placeholder="Search for building materials..."
                  value={searchValue}
                  onChange={handleSearchChange}
                  inputProps={{ 'aria-label': 'search products' }}
                />
                {/* Microphone button for voice search */}
                <Tooltip title={isRecording ? 'Stop voice search' : 'Search by voice'}>
                  <IconButton
                    type="button"
                    aria-label={isRecording ? 'stop voice search' : 'start voice search'}
                    onClick={handleVoiceMicClick}
                    sx={{
                      p: '8px',
                      color: isRecording ? 'error.main' : 'action.active',
                      ...(isRecording && {
                        animation: 'mos-mic-pulse 1.2s ease-in-out infinite',
                        '@keyframes mos-mic-pulse': {
                          '0%, 100%': { opacity: 1 },
                          '50%': { opacity: 0.4 },
                        },
                      }),
                    }}
                  >
                    {isRecording ? (
                      <StopIcon fontSize="small" />
                    ) : (
                      <MicIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Paper>

              {/* Autocomplete suggestions dropdown */}
              {autocompleteSuggestions.length > 0 && (
                <Paper
                  elevation={4}
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    mt: 0.5,
                    borderRadius: 2,
                    zIndex: 1400,
                    maxHeight: 280,
                    overflow: 'auto',
                  }}
                >
                  <List dense disablePadding>
                    {autocompleteSuggestions.map((suggestion) => {
                      const isGroup = suggestion.type === 'GROUP';
                      const itemKey = isGroup ? `group-${suggestion.name}` : suggestion.id;
                      return (
                        <ListItemButton
                          key={itemKey}
                          onClick={() => handleSuggestionClick(suggestion)}
                          sx={{ py: 1, px: 2, '&:hover': { bgcolor: 'action.hover' } }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {isGroup
                              ? <CategoryIcon fontSize="small" sx={{ color: 'primary.main' }} />
                              : <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            }
                          </ListItemIcon>
                          <ListItemText
                            primary={suggestion.name}
                            primaryTypographyProps={{
                              variant: 'body2',
                              fontWeight: isGroup ? 600 : 400,
                            }}
                            secondary={isGroup ? `${suggestion.count} variant${suggestion.count !== 1 ? 's' : ''}` : undefined}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Paper>
              )}
            </Box>
          </Box>
          
          {/* Right side actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Role-specific quick actions */}
            {user?.role === 'ADMIN' && (
              <Tooltip title="Admin Panel">
                <IconButton
                  color="primary"
                  onClick={() => navigate('/admin')}
                  sx={{ display: { xs: 'none', sm: 'flex' } }}
                >
                  <AdminIcon />
                </IconButton>
              </Tooltip>
            )}
            
            {/* Wishlist - For authenticated customers and sellers */}
            {isAuthenticated && (!user?.role || user?.role === 'CUSTOMER' || user?.role === 'SELLER') && (
              <Tooltip title="Wishlist">
                <IconButton
                  color="inherit"
                  onClick={handleWishlist}
                  sx={{ display: { xs: 'none', sm: 'flex' } }}
                >
                  <Badge badgeContent={wishlistItemsCount} color="error">
                    <WishlistIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}
            
            {/* Notifications */}
            <NotificationSystem />
            
            {/* Cart - For authenticated customers/sellers and guest users with items in cart */}
            {((isAuthenticated && (!user?.role || user?.role === 'CUSTOMER' || user?.role === 'SELLER')) || (!isAuthenticated && cartItemCount > 0)) && (
              <Tooltip title="Shopping Cart">
                <IconButton color="inherit" onClick={handleCart}>
                  <Badge badgeContent={cartItemCount} color="error">
                    <CartIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}
            
            {/* User Menu */}
            {isAuthenticated ? (
              <IconButton
                edge="end"
                aria-label="account of current user"
                aria-controls="primary-search-account-menu"
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
                sx={{ display: { xs: 'none', sm: 'flex' } }}
              >
                <Avatar
                  src={businessLogoUrl || undefined}
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: 'primary.main',
                    border: '2px solid #e0e0e0',
                    fontSize: '0.875rem',
                  }}
                >
                  {!businessLogoUrl && (user?.firstName?.charAt(0)?.toUpperCase() || 'U')}
                </Avatar>
              </IconButton>
            ) : (
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1 }}>
                <Button
                  color="inherit"
                  onClick={handleLogin}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  Login
                </Button>
                <Button
                  variant="contained"
                  onClick={handleRegister}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #2C3E50,#2C3E50)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2C3E50, #2C3E50)',
                    },
                  }}
                >
                  Register
                </Button>
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={isMenuOpen}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 2,
            minWidth: 200,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1,
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {user?.firstName} {user?.lastName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.role || 'Customer'}
          </Typography>
        </Box>
        
        <MenuItem onClick={handleProfile}>
          <PersonIcon sx={{ mr: 2 }} />
          My Profile
        </MenuItem>
        
        <MenuItem onClick={handleDashboard}>
          <DashboardIcon sx={{ mr: 2 }} />
          {
            user?.role === 'ADMIN' ? 'Admin Panel' :
            user?.role === 'SELLER' ? 'Seller Dashboard' :
            'My Account'
          }
        </MenuItem>
        
        {(!user?.role || user?.role === 'CUSTOMER') && (
          <MenuItem onClick={() => { navigate('/orders'); handleMenuClose(); }}>
            <ShoppingCart sx={{ mr: 2 }} />
            My Orders
          </MenuItem>
        )}
        
        <Divider />
        
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <LogoutIcon sx={{ mr: 2 }} />
          Logout
        </MenuItem>
      </Menu>
      
      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
        PaperProps={{
          sx: {
            width: 280,
            bgcolor: 'background.paper',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
          <Box
            component="img"
            src="/mos-logo.jpg"
            alt="MOS - Stone Shopping Simplified"
            onClick={handleLogoClick}
            sx={{
              height: 60,
              width: 'auto',
              cursor: 'pointer',
              '&:hover': {
                transform: 'scale(1.05)',
                transition: 'transform 0.2s ease-in-out',
              },
            }}
          />
        </Box>
        <Divider />
        <List>
          {mobileMenuItems.map((item, index) => (
            <ListItem key={index} disablePadding>
              <ListItemButton
                onClick={() => {
                  if (item.action) {
                    item.action();
                  } else {
                    navigate(item.path);
                  }
                  dispatch(toggleMobileMenu());
                }}
                selected={location.pathname === item.path}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* Voice search error notification */}
      <Snackbar
        open={voiceErrorOpen}
        autoHideDuration={5000}
        onClose={() => setVoiceErrorOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setVoiceErrorOpen(false)}
          severity="warning"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {voiceError}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Navbar;